import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchGrammarLesson, completeGrammarLessonSection, completeGrammarLessonQuiz } from "../../api";
import { MixedText } from "./shared";
import TopicCategoryPicker from "../TopicCategoryPicker";
import { useLessonAudio } from "./useLessonAudio";
import NounSwapIntro from "./activities/NounSwapIntro";
import PronounExplorer from "./activities/PronounExplorer";
import PersonSortGame from "./activities/PersonSortGame";
import ScenarioPick from "./activities/ScenarioPick";
import PronounFillBlank from "./activities/PronounFillBlank";
import AttachedTeaser from "./activities/AttachedTeaser";
import MissingIsIntro from "./activities/MissingIsIntro";
import SentenceAnatomy from "./activities/SentenceAnatomy";
import PartTapGame from "./activities/PartTapGame";
import GenderMorph from "./activities/GenderMorph";
import WordCards from "./activities/WordCards";
import QuestionFlip from "./activities/QuestionFlip";
import DialoguePlay from "./activities/DialoguePlay";
import PointReveal from "./activities/PointReveal";
import DefiniteMorph from "./activities/DefiniteMorph";
import AdjectiveFlip from "./activities/AdjectiveFlip";
import SuffixBuilder from "./activities/SuffixBuilder";
import HaveVanish from "./activities/HaveVanish";
import PrepScene from "./activities/PrepScene";
import NumberTiles from "./activities/NumberTiles";
import CountUp from "./activities/CountUp";
import VerbMachine from "./activities/VerbMachine";
import RoutineReveal from "./activities/RoutineReveal";
import ChunkBuilder from "./activities/ChunkBuilder";
import NoStamp from "./activities/NoStamp";
import GroupMerge from "./activities/GroupMerge";
import QuestionMatch from "./activities/QuestionMatch";
import ObjectSwap from "./activities/ObjectSwap";
import TenseShift from "./activities/TenseShift";
import RootRecast from "./activities/RootRecast";
import GrammarLessonQuiz from "./activities/GrammarLessonQuiz";

// section.kind -> renderer
const ACTIVITIES = {
  noun_swap: NounSwapIntro,
  pronoun_grid: PronounExplorer,
  person_sort: PersonSortGame,
  bucket_sort: PersonSortGame,
  scenario_pick: ScenarioPick,
  fill_blank: PronounFillBlank,
  attached_builder: AttachedTeaser,
  verb_vanish: MissingIsIntro,
  sentence_anatomy: SentenceAnatomy,
  part_tap: PartTapGame,
  gender_morph: GenderMorph,
  word_cards: WordCards,
  question_flip: QuestionFlip,
  dialogue_play: DialoguePlay,
  point_reveal: PointReveal,
  definite_morph: DefiniteMorph,
  adjective_flip: AdjectiveFlip,
  suffix_build: SuffixBuilder,
  have_vanish: HaveVanish,
  prep_scene: PrepScene,
  number_tiles: NumberTiles,
  count_up: CountUp,
  verb_machine: VerbMachine,
  routine_reveal: RoutineReveal,
  chunk_builder: ChunkBuilder,
  no_stamp: NoStamp,
  group_merge: GroupMerge,
  answer_match: QuestionMatch,
  object_swap: ObjectSwap,
  tense_shift: TenseShift,
  root_recast: RootRecast,
};

function CheckIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 10.5l3.2 3.2L15 7" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CIRCLE_STYLE = {
  done: { background: "var(--gold)", borderColor: "var(--gold)", color: "var(--ink)" },
  current: {
    background: "var(--ink)",
    borderColor: "var(--gold)",
    color: "var(--gold-bright)",
    boxShadow: "0 0 0 4px rgba(201,162,39,0.15)",
  },
  upcoming: { background: "var(--ink)", borderColor: "var(--gold-dim)", color: "var(--parchment-dim)" },
};

/**
 * GrammarLessonView — dedicated renderer for hardcoded interactive grammar
 * lessons (issue #122). Replaces TopicDetail/GuidedTopicJourney for topics
 * flagged is_grammar_lesson: loads the lesson + per-user progress, walks the
 * gated sections, and reports completions so the sidebar progress stays live.
 *
 * Props:
 *   topicId     — the grammar-lesson Topic id
 *   onBack()    — return to the topic list
 *   onProgress()— refresh App-level progress (sidebar bars/checkmarks)
 */
export default function GrammarLessonView({
  topicId,
  onBack,
  onProgress,
  // Admin-only sidebar category picker (grammar lessons are Topics too, so
  // they can be filed under a category like any other topic).
  isAdmin = false,
  categories = [],
  categoryId = null,
  onCategoryChanged,
}) {
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [viewKey, setViewKey] = useState(null);
  const inFlightRef = useRef(new Set());
  const audio = useLessonAudio();

  useEffect(() => {
    let cancelled = false;
    setLesson(null);
    setProgress(null);
    setError(null);
    fetchGrammarLesson(topicId)
      .then((data) => {
        if (cancelled) return;
        setLesson(data.lesson);
        setProgress(data.progress);
        const sectionKeys = data.lesson.sections.map((s) => s.key);
        const firstIncomplete = sectionKeys.find((k) => !data.progress.completed_sections.includes(k));
        setViewKey(firstIncomplete || "quiz");
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [topicId]);

  const sectionKeys = useMemo(() => (lesson ? lesson.sections.map((s) => s.key) : []), [lesson]);
  const completedSections = useMemo(() => (progress ? progress.completed_sections : []), [progress]);
  const allSectionsDone = sectionKeys.length > 0 && sectionKeys.every((k) => completedSections.includes(k));

  const steps = useMemo(() => {
    if (!lesson) return [];
    const sectionSteps = lesson.sections.map((s, i) => ({
      key: s.key,
      order: i + 1,
      title: s.title,
      titleArabic: s.title_arabic,
      completed: completedSections.includes(s.key),
      unlocked: i === 0 || completedSections.includes(lesson.sections[i - 1].key) || completedSections.includes(s.key),
    }));
    return [
      ...sectionSteps,
      {
        key: "quiz",
        order: lesson.sections.length + 1,
        title: lesson.quiz.title,
        titleArabic: lesson.quiz.title_arabic,
        completed: !!progress?.quiz_passed,
        unlocked: allSectionsDone,
      },
    ];
  }, [lesson, completedSections, allSectionsDone, progress]);

  const markSectionDone = useCallback(
    async (sectionKey) => {
      if (inFlightRef.current.has(sectionKey)) return;
      inFlightRef.current.add(sectionKey);
      try {
        const data = await completeGrammarLessonSection(topicId, sectionKey);
        setProgress(data.progress);
        if (onProgress) onProgress();
      } catch {
        inFlightRef.current.delete(sectionKey); // let a retry happen
      }
    },
    [topicId, onProgress]
  );

  const submitQuizScore = useCallback(
    async (scorePercent) => {
      const data = await completeGrammarLessonQuiz(topicId, scorePercent);
      setProgress(data.progress);
      if (onProgress) onProgress();
    },
    [topicId, onProgress]
  );

  if (error) {
    return (
      <div className="dark-scope p-6">
        <p className="text-sm" style={{ color: "#e58c8c" }}>{error}</p>
        <button type="button" onClick={onBack} className="text-sm underline mt-2" style={{ color: "var(--parchment)" }}>
          Back to topics
        </button>
      </div>
    );
  }
  if (!lesson || !progress) {
    return (
      <div className="dark-scope flex items-center justify-center py-16">
        <p className="text-sm" style={{ color: "var(--parchment-dim)" }}>Loading lesson…</p>
      </div>
    );
  }

  const activeStep = steps.find((s) => s.key === viewKey) || steps[0];
  const activeSection = lesson.sections.find((s) => s.key === viewKey);
  const nextLockedAfterActive = steps.find((s) => !s.completed && s.key !== activeStep.key && s.order > activeStep.order);
  const pct = progress.progress_percent;

  const renderStepper = (compact) => (
    <ol className={`flex items-start list-none m-0 ${compact ? "sm:hidden px-2 pt-3 pb-1" : "hidden sm:flex px-2 pt-4 pb-2"}`}>
      {steps.map((step, i) => {
        const state = step.completed ? "done" : step.key === viewKey ? "current" : "upcoming";
        const interactive = step.unlocked;
        const size = compact ? "w-9 h-9 text-base" : "w-11 h-11 text-lg";
        return (
          <li key={step.key} className="flex-1 flex flex-col items-center relative min-w-0">
            {i > 0 && (
              <span
                aria-hidden="true"
                className={`absolute ${compact ? "top-[18px]" : "top-[22px]"} right-1/2 left-[-50%] h-px`}
                style={{ background: "rgba(201,162,39,0.22)" }}
              />
            )}
            <button
              type="button"
              onClick={interactive ? () => setViewKey(step.key) : undefined}
              disabled={!interactive}
              aria-current={step.key === viewKey ? "step" : undefined}
              title={step.title}
              className={`relative z-10 flex flex-col items-center gap-2 w-full px-1 ${interactive ? "cursor-pointer" : "cursor-not-allowed"}`}
            >
              <span
                className={`flex items-center justify-center rounded-full border-2 font-cormorant font-semibold transition-all ${size}`}
                style={CIRCLE_STYLE[state]}
              >
                {step.completed ? <CheckIcon className="w-4 h-4" /> : step.order}
              </span>
              <span
                className={`font-cormorant font-semibold text-center leading-tight truncate max-w-full ${compact ? "text-[0.7rem]" : "text-base"}`}
                style={{ color: step.key === viewKey ? "var(--gold-bright)" : step.completed ? "var(--parchment)" : "var(--parchment-dim)" }}
              >
                {step.title}
              </span>
              {!compact && (
                <span className="text-[0.76rem] leading-none" style={{ color: "var(--parchment-dim)" }}>
                  {step.completed ? "Completed" : step.key === viewKey ? "In progress" : step.unlocked ? "Ready" : "Locked"}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );

  const ActivityComponent = activeSection ? ACTIVITIES[activeSection.kind] : null;

  return (
    <div className="dark-scope">
      {/* ── Lesson header card ── */}
      <section aria-label="Grammar lesson" className="surface-glass mb-[22px] pb-4" style={{ borderRadius: 14 }}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={onBack}
                aria-label="Back to topics"
                className="inline-flex items-center gap-1 text-sm transition-[filter] hover:brightness-125"
                style={{ color: "var(--parchment-dim)" }}
              >
                ← Topics
              </button>
              <span
                className="inline-flex items-center rounded-2xl px-2.5 py-0.5 text-[0.72rem] font-semibold uppercase tracking-wide"
                style={{ background: "var(--tag-violet-bg)", color: "var(--tag-violet)" }}
              >
                Grammar
              </span>
            </div>
            <h2 dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7] mt-3 mb-1 text-left" style={{ color: "var(--gold-bright)" }}>
              {lesson.title_arabic}
            </h2>
            <h3 className="font-cormorant font-semibold text-xl leading-tight mt-0 mb-0" style={{ color: "var(--parchment)" }}>
              {lesson.title}
            </h3>
            <p className="text-sm leading-tight mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>{lesson.tagline}</p>
            {/* Phone: the picker gets its own row so it doesn't squeeze the title column. */}
            {isAdmin && (
              <div className="md:hidden mt-2">
                <TopicCategoryPicker topicId={topicId} categoryId={categoryId} categories={categories} onChanged={onCategoryChanged} />
              </div>
            )}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
          {isAdmin && (
            <div className="hidden md:block">
              <TopicCategoryPicker topicId={topicId} categoryId={categoryId} categories={categories} onChanged={onCategoryChanged} />
            </div>
          )}
          <span
            className="shrink-0 inline-flex items-center gap-1.5 rounded-2xl px-3 py-1 text-[0.78rem] whitespace-nowrap"
            style={{ color: "var(--gold-bright)", border: "1px solid var(--panel-line)" }}
          >
            {progress.completed ? (
              <>
                <CheckIcon className="w-3.5 h-3.5" />
                Completed
              </>
            ) : (
              <>{pct}%</>
            )}
          </span>
          </div>
        </div>

        <div className="px-5 pt-3.5 pb-1">
          <div className="progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Lesson completion">
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {renderStepper(false)}
        {renderStepper(true)}
      </section>

      {/* ── Section head ── */}
      <div className="flex items-center gap-3 mb-4">
        <span
          className="inline-flex items-center justify-center rounded-full text-[0.85rem] font-bold"
          style={{ width: 26, height: 26, background: "var(--gold)", color: "var(--ink)" }}
        >
          {activeStep.order}
        </span>
        <h2 className="font-cormorant font-semibold text-[1.4rem] m-0" style={{ color: "var(--parchment)" }}>
          {activeStep.title}
        </h2>
        <span dir="rtl" lang="ar" className="font-arabic text-lg" style={{ color: "var(--parchment-dim)" }}>
          {activeStep.titleArabic}
        </span>
        {activeStep.completed && (
          <span className="inline-flex items-center gap-1.5 text-[0.8rem]" style={{ color: "var(--teal)" }}>
            <CheckIcon className="w-3.5 h-3.5" /> Completed
          </span>
        )}
      </div>

      {/* ── Section instructions (every section explains what to do) ── */}
      {viewKey !== "quiz" && activeSection?.intro && (
        <p className="text-sm leading-relaxed max-w-3xl mb-4" style={{ color: "var(--parchment-dim)" }}>
          <MixedText text={activeSection.intro} />
        </p>
      )}

      {/* ── Active content ── */}
      {viewKey === "quiz" ? (
        <GrammarLessonQuiz quiz={lesson.quiz} progress={progress} onSubmitScore={submitQuizScore} audio={audio} />
      ) : ActivityComponent ? (
        <ActivityComponent
          key={viewKey}
          data={{ ...activeSection.data, intro: activeSection.intro }}
          onComplete={() => markSectionDone(activeSection.key)}
          audio={audio}
        />
      ) : null}

      {/* ── Continue band ── */}
      {activeStep.completed && viewKey !== "quiz" && nextLockedAfterActive && (
        <div className="surface-glass flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 mt-4 px-5 py-3 rounded-xl">
          <p className="text-xs leading-snug m-0" style={{ color: "var(--parchment-dim)" }}>
            Section done — keep the momentum going.
          </p>
          <button
            type="button"
            onClick={() => setViewKey(nextLockedAfterActive.key)}
            className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            Continue → {nextLockedAfterActive.title}
          </button>
        </div>
      )}

      {progress.completed && (
        <div
          className="flex items-center gap-2 mt-4 px-5 py-3 rounded-xl"
          style={{ border: "1px solid var(--panel-line)", background: "var(--gold-bg)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">🎉</span>
          <p className="text-xs font-medium m-0" style={{ color: "var(--parchment)" }}>
            Lesson complete — best score {progress.quiz_best_score}%. Tap any step above to review or replay it.
          </p>
        </div>
      )}
    </div>
  );
}
